/* eslint-disable no-console */
import '@ghq-abi/design-system/css';
import '@ghq-abi/design-system-v2/css';
import 'core-js/actual';

import * as React from 'react';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import type { AppProps } from 'next/app';
import NextNProgress from 'nextjs-progressbar';
import { AuthClientProvider } from '@ghq-abi/auth-client-lib/client';
import { DesignSystemProvider, theme } from '@ghq-abi/design-system';
import { ThemeProvider } from '@ghq-abi/design-system-v2';
import {
  Hydrate,
  QueryClient,
  QueryClientProvider,
} from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { FormatIcu } from '@tolgee/format-icu';
import {
  BackendFetch,
  DevTools,
  Tolgee,
  TolgeeProvider,
  useTolgeeSSR,
} from '@tolgee/react';

import { ErrorBoundary } from '~/app/components';
import { AbilityProvider } from '~/app/contexts/AbilityContext';
import { FlagsProvider } from '~/app/contexts/FlagsContext';
import { FullscreenProvider } from '~/app/contexts/FullscreenContext';
import MixPanelProvider from '~/app/contexts/MixPanelContext';
import { NavProvider } from '~/app/contexts/NavContext';
import { ProfilePictureProvider } from '~/app/contexts/ProfilePictureContext';
import { ToastProvider } from '~/app/contexts/ToastContext';
import { UserProvider } from '~/app/contexts/UserContext';
import { globalStyles } from '~/app/styles/global';
import { AppTemplate } from '~/app/templates/App';
import {
  AppAbility,
  createUserAbility,
  Subjects,
} from '~/shared/auth/permissions';
import { OprSession } from '~/shared/auth/types/session';
import { NPS } from '~/shared/components';
import { CES } from '~/shared/components/MetricsCes';
import { DEFAULT_LANGUAGE } from '~/shared/constants/i18n';
import {
  GA,
  initializeAxeCore,
  queryClientConfig,
  useDatadog,
  useSetupClarityTracking,
} from '~/shared/lib';
import { getEnv, isClient, isDevelopment } from '~/shared/utils/environments';

import '../app/globals.css';

if (isClient() && isDevelopment()) {
  initializeAxeCore();
}

globalStyles();

const appEnv = getEnv();

type CustomAppProps = {
  Component: AppProps['Component'] & { unauth?: boolean };
  pageProps: AppProps['pageProps'] & {
    session: OprSession;
    now?: number;
    dehydratedState?: unknown;
    abilityRules?: AppAbility['_indexedRules'];
    abilityPermissions?: Subjects;
    abilityRulesOverride?: AppAbility['_indexedRules'];
  };
};

const tolgee = Tolgee()
  .use(BackendFetch())
  .use(DevTools())
  .use(FormatIcu())
  .init({
    defaultLanguage: 'en-US',
    defaultNs: 'web-app',
    apiKey: process.env.NEXT_PUBLIC_TOLGEE_API_KEY,
    apiUrl: process.env.NEXT_PUBLIC_TOLGEE_API_URL,
  });

export default function App({
  Component,
  pageProps: {
    session,
    now,
    abilityRules,
    isMobile,
    flags,
    abilityPermissions = {},
    abilityRulesOverride,
    ...pageProps
  },
}: CustomAppProps) {
  // https://tanstack.com/query/v4/docs/react/guides/ssr#using-hydration
  const [queryClient] = React.useState(
    () => new QueryClient(queryClientConfig)
  );
  const locale = session?.user?.preferredLanguage ?? DEFAULT_LANGUAGE;

  useDatadog(locale, session);
  const ssrTolgee = useTolgeeSSR(tolgee, locale);

  useSetupClarityTracking(locale, session);

  return (
    <TolgeeProvider tolgee={ssrTolgee}>
      <ErrorBoundary>
        <AuthClientProvider session={session} refetchOnWindowFocus={false}>
          <QueryClientProvider client={queryClient}>
            <ThemeProvider>
              <DesignSystemProvider>
                {Component.unauth ? (
                  <Component {...pageProps} />
                ) : (
                  <UserProvider session={session}>
                    <ToastProvider>
                      <AbilityProvider
                        ability={createUserAbility(
                          session?.user,
                          abilityPermissions,
                          abilityRulesOverride ?? abilityRules
                        )}
                      >
                        <FullscreenProvider
                          startState={pageProps.startFullscreenState}
                        >
                          <FlagsProvider flags={flags}>
                            <NavProvider>
                              <ProfilePictureProvider>
                                <MixPanelProvider>
                                  <Hydrate state={pageProps.dehydratedState}>
                                    <AppTemplate session={session}>
                                      <DndProvider backend={HTML5Backend}>
                                        <Component {...pageProps} />
                                      </DndProvider>
                                    </AppTemplate>
                                  </Hydrate>
                                </MixPanelProvider>
                                <NextNProgress
                                  color={theme.colors.black.toString()}
                                  startPosition={0.3}
                                  stopDelayMs={200}
                                  height={3}
                                />
                              </ProfilePictureProvider>
                            </NavProvider>
                          </FlagsProvider>
                        </FullscreenProvider>
                      </AbilityProvider>
                    </ToastProvider>
                  </UserProvider>
                )}
              </DesignSystemProvider>
            </ThemeProvider>
            <ReactQueryDevtools />
          </QueryClientProvider>
        </AuthClientProvider>

        {/* {['production', 'staging'].includes(appEnv) && (
            <DelightedScript session={session} />
          )} */}
        {['production', 'staging', 'dev'].includes(appEnv) && (
          <NPS.Script
            globalId={session?.user?.globalId ? session.user.globalId : null}
            preferredLanguage={locale}
          />
        )}
        {['staging', 'dev'].includes(appEnv) && (
          <CES.Script
            globalId={session?.user?.globalId ? session.user.globalId : null}
            preferredLanguage={locale}
          />
        )}
      </ErrorBoundary>

      <GA.Script session={session} />
    </TolgeeProvider>
  );
}
